/**
 * @fileoverview Contact page with form and contact information
 */

import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MessageSquare, Send, CheckCircle, Bug, AlertTriangle, Shield } from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const Contact: React.FC = () => {
     const [formData, setFormData] = useState({
     name: '',
     email: '',
     contactType: 'General Inquiry',
     subject: '',
     message: '',
     browserInfo: '',
     appVersion: ''
   });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [humanVerification, setHumanVerification] = useState({
    num1: 0,
    num2: 0,
    userAnswer: '',
    isCorrect: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear any submit message when user starts typing
    if (submitMessage) {
      setSubmitMessage(null);
    }
  };

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserInfo = {
      userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenResolution: `${screen.width}x${screen.height}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    return JSON.stringify(browserInfo);
  };

  const handleGetBrowserInfo = () => {
    const browserInfo = getBrowserInfo();
    setFormData(prev => ({
      ...prev,
      browserInfo
    }));
  };

  const handleGetAppVersion = () => {
    // Get app version from package.json or environment
    const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    setFormData(prev => ({
      ...prev,
      appVersion
    }));
  };

  // Generate random numbers for human verification
  const generateVerificationNumbers = () => {
    const num1 = Math.floor(Math.random() * 10) + 1; // 1-10
    const num2 = Math.floor(Math.random() * 10) + 1; // 1-10
    setHumanVerification(prev => ({
      ...prev,
      num1,
      num2,
      userAnswer: '',
      isCorrect: false
    }));
    
    // Clear any submit message when generating new numbers
    if (submitMessage) {
      setSubmitMessage(null);
    }
  };

  // Handle human verification answer
  const handleVerificationAnswer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userAnswer = e.target.value;
    const correctAnswer = humanVerification.num1 + humanVerification.num2;
    const isCorrect = parseInt(userAnswer) === correctAnswer;
    
    setHumanVerification(prev => ({
      ...prev,
      userAnswer,
      isCorrect
    }));
    
    // Clear any submit message when user changes verification answer
    if (submitMessage) {
      setSubmitMessage(null);
    }
  };

  // Generate verification numbers on component mount
  useEffect(() => {
    generateVerificationNumbers();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Import the contact form service
      const { contactFormService } = await import('../../services/contactFormService');
      
      // Prepare contact data
      const contactData = {
        name: formData.name,
        email: formData.email,
        contactType: formData.contactType,
        subject: formData.subject,
        message: formData.message,
        browserInfo: formData.browserInfo || getBrowserInfo(),
        appVersion: formData.appVersion || ''
      };

      // Submit to Firestore
      const result = await contactFormService.submitContactForm(contactData);
      
      if (result.success) {
        // Success - show confirmation
        setSubmitMessage({ type: 'success', text: 'Request submitted successfully! We\'ll get back to you soon.' });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          contactType: 'General Inquiry',
          subject: '',
          message: '',
          browserInfo: '',
          appVersion: ''
        });
        
        // Generate new verification numbers
        generateVerificationNumbers();
      } else {
        throw new Error(result.error || 'Failed to submit form');
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage({ type: 'error', text: 'Failed to send message. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };



  const reportTypes = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Bug Report",
      description: "Report technical issues, glitches, or app malfunctions",
      examples: [
        "App crashes when spinning",
        "Buttons not responding",
        "Display issues",
        "Performance problems"
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Content Issue",
      description: "Report inappropriate, offensive, or incorrect content",
      examples: [
        "Inappropriate questions",
        "Offensive language",
        "Incorrect information",
        "Spam or irrelevant content"
      ]
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Feature Request",
      description: "Suggest new features or improvements",
      examples: [
        "New game types",
        "Additional customization options",
        "Performance improvements",
        "User interface enhancements"
      ]
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Other Issues",
      description: "Report any other problems or concerns",
      examples: [
        "Account issues",
        "Privacy concerns",
        "Accessibility problems",
        "General feedback"
      ]
    }
  ];

     const contactTypes = [
     {
       value: 'General Inquiry',
       label: 'General Inquiry',
       icon: <MessageSquare className="w-4 h-4" />,
       description: 'General questions or feedback'
     },
     {
       value: 'Bug Report',
       label: 'Bug Report',
       icon: <Bug className="w-4 h-4" />,
       description: 'Report technical issues, glitches, or app malfunctions',
       examples: [
         "App crashes when spinning",
         "Buttons not responding", 
         "Display issues",
         "Performance problems"
       ]
     },
     {
       value: 'Feature Request',
       label: 'Feature Request',
       icon: <Send className="w-4 h-4" />,
       description: 'Suggest new features or improvements',
       examples: [
         "New game types",
         "Additional customization options",
         "Performance improvements",
         "User interface enhancements"
       ]
     },
     {
       value: 'Content Issue',
       label: 'Content Issue',
       icon: <AlertTriangle className="w-4 h-4" />,
       description: 'Report inappropriate, offensive, or incorrect content',
       examples: [
         "Inappropriate questions",
         "Offensive language",
         "Incorrect information",
         "Spam or irrelevant content"
       ]
     },
     {
       value: 'Account Help',
       label: 'Account Help',
       icon: <Mail className="w-4 h-4" />,
       description: 'Account creation, login issues, or data management'
     },
     {
       value: 'Business Inquiry',
       label: 'Business Inquiry',
       icon: <Mail className="w-4 h-4" />,
       description: 'Partnership opportunities or business questions'
     }
   ];

  const reportGuidelines = [
    "Be specific and provide detailed information about the issue",
    "Include steps to reproduce the problem if applicable",
    "Mention your device type and browser version",
    "Provide screenshots if possible",
    "Be respectful and constructive in your feedback"
  ];



  return (
    <HelpPageLayout>
      <Helmet>
        <title>Contact Us - Spinner Games !!</title>
        <meta name="description" content="Get in touch with our support team. We're here to help with any questions or issues you may have." />
                  <meta property="og:title" content="Contact Us - Spinner Games !!" />
        <meta property="og:description" content="Contact our support team for help and assistance." />
        <link rel="canonical" href="https://spinnergames.fun/help/contact" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Us",
          "description": "Contact page for Spinner Games !!",
          "mainEntity": {
            "@type": "Organization",
            "name": "Spinner Games !!",
            "email": "support@spinnergames.fun",
            "telephone": "+1 (555) 123-4567"
          }
        })}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            We are happy to help you!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Side - Instructions and Guidelines */}
          <div className="space-y-6 order-1 lg:order-1">
            {/* Compact Reporting Guidelines */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Reporting Guidelines
              </h2>
              <div className="space-y-2">
                {reportGuidelines.map((guideline, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {guideline}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Before You Contact Us and Feedback Welcome Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-5 text-center">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Before You Contact Us
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Check our <a href="/help/how-to-play" className="text-blue-600 dark:text-blue-400 hover:underline">How to Play</a> guide 
                  for quick answers to common questions.
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-5 text-center">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
                  Feedback Welcome
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We love hearing from our users! Share your ideas and suggestions to help us improve.
                </p>
              </div>
            </div>

            {/* What Would You Like to Report? */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                What Would You Like to Report?
              </h2>
              <div className="space-y-4">
                {reportTypes.map((type, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center text-red-600 dark:text-red-300 flex-shrink-0">
                        {type.icon}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                          {type.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {type.description}
                        </p>
                      </div>
                    </div>
                    <div className="ml-11">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Examples:</h4>
                      <ul className="space-y-1">
                        {type.examples.map((example, exampleIndex) => (
                          <li key={exampleIndex} className="text-xs text-gray-600 dark:text-gray-300">
                            • {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 order-2 lg:order-2">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Send us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Name *
                  </label>
                                     <input
                     type="text"
                     id="name"
                     name="name"
                     value={formData.name}
                     onChange={handleInputChange}
                     required
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="Your name"
                   />
                </div>

                <div>
                  <label htmlFor="email" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                                     <input
                     type="email"
                     id="email"
                     name="email"
                     value={formData.email}
                     onChange={handleInputChange}
                     required
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="your.email@example.com"
                   />
                </div>

                <div>
                  <label htmlFor="contactType" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Type *
                  </label>
                                     <select
                     id="contactType"
                     name="contactType"
                     value={formData.contactType}
                     onChange={handleInputChange}
                     required
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                   >
                    {contactTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subject *
                  </label>
                                     <input
                     type="text"
                     id="subject"
                     name="subject"
                     value={formData.subject}
                     onChange={handleInputChange}
                     required
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="Brief description of your inquiry"
                   />
                </div>

                <div>
                  <label htmlFor="message" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                                     <textarea
                     id="message"
                     name="message"
                     value={formData.message}
                     onChange={handleInputChange}
                     required
                     rows={4}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="Tell us how we can help you..."
                   />
                </div>

                {/* Browser Information */}
                <div>
                  <label htmlFor="browserInfo" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Browser Information
                  </label>
                  <div className="mb-2">
                                         <button
                       type="button"
                       onClick={handleGetBrowserInfo}
                       className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                     >
                       Get Browser Details
                     </button>
                  </div>
                                     <textarea
                     id="browserInfo"
                     name="browserInfo"
                     value={formData.browserInfo}
                     onChange={handleInputChange}
                     rows={3}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="Browser information will be collected automatically when you click the button above..."
                   />
                </div>

                                 {/* App Version */}
                 <div>
                   <label htmlFor="appVersion" className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-2">
                     App Version
                   </label>
                   <div className="mb-2">
                     <button
                       type="button"
                       onClick={handleGetAppVersion}
                       className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                     >
                       Get App Version
                     </button>
                   </div>
                   <input
                     type="text"
                     id="appVersion"
                     name="appVersion"
                     value={formData.appVersion}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base"
                     placeholder="e.g., 1.0.0 (optional but helpful for bug reports)"
                   />
                 </div>

                {/* Human Verification */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <label className="block text-base font-medium text-gray-700 dark:text-gray-300 mb-3">
                    I am Human Verification *
                  </label>
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {humanVerification.num1} + {humanVerification.num2} =
                    </span>
                    <input
                      type="text"
                      value={humanVerification.userAnswer}
                      onChange={handleVerificationAnswer}
                      className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-base text-center"
                      placeholder="?"
                      required
                    />
                    <button
                      type="button"
                      onClick={generateVerificationNumbers}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      New Problem
                    </button>
                  </div>
                  {humanVerification.userAnswer && (
                    <div className="mt-2">
                      {humanVerification.isCorrect ? (
                        <div className="flex items-center text-green-600 dark:text-green-400">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          <span className="text-sm">Correct! You can now submit the form.</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600 dark:text-red-400">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          <span className="text-sm">Incorrect answer. Please try again.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                                 <div className="pt-2">
                   <button
                     type="submit"
                     disabled={isSubmitting || !humanVerification.isCorrect}
                     className="w-full flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
                   >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit
                    </>
                  )}
                  </button>
                  
                  {/* Success/Error Message */}
                  {submitMessage && (
                    <div className={`mt-3 p-3 rounded-lg text-center ${
                      submitMessage.type === 'success' 
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    }`}>
                      <div className={`flex items-center justify-center ${
                        submitMessage.type === 'success' 
                          ? 'text-green-700 dark:text-green-300' 
                          : 'text-red-700 dark:text-red-300'
                      }`}>
                        {submitMessage.type === 'success' ? (
                          <>
                            <CheckCircle className="w-5 h-5 mr-2" />
                            <span className="font-medium">{submitMessage.text}</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-5 h-5 mr-2" />
                            <span className="font-medium">{submitMessage.text}</span>
                          </>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              </form>
          </div>
        </div>



        {/* Back to Help */}
        <div className="text-center mt-12">
          <a
            href="/help"
            className="inline-flex items-center px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors"
          >
            Back to Help Center
          </a>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default Contact; 